// scheduling/components/OutlookSyncSetting.jsx
// Outlook Calendar Sync — per-user opt-in toggle + manual "Sync now" + status.
// Lives inside the Settings modal (relocated from the staff profile in V0). The
// opt-in value is controlled by the parent (AppShell owns it); this component owns
// only its transient busy/status state. See Trello #133.

import { useState } from 'react';
import { setOutlookOptIn, reconcileOutlook } from '../services/schedApi';

function summarize(res) {
  const s = res && res.synced && res.synced[0];
  return s
    ? `Synced — ${s.created || 0} added, ${s.updated || 0} updated, ${s.deleted || 0} removed.`
    : 'Sync started.';
}

export default function OutlookSyncSetting({ staffId, optIn, onOptInChange, highlight }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const handleToggle = async (next) => {
    if (!staffId) return;
    setBusy(true); setMsg('');
    try {
      await setOutlookOptIn(staffId, next);
      onOptInChange?.(next);
      // Enable -> backfill; disable -> purge existing RPRS events from the mailbox.
      const res = next
        ? await reconcileOutlook([staffId])
        : await reconcileOutlook([staffId], { purge: true });
      const s = res && res.synced && res.synced[0];
      setMsg(next
        ? summarize(res)
        : (s ? `Turned off — ${s.deleted || 0} RPRS event(s) removed from Outlook.` : 'Turned off.'));
    } catch (err) {
      onOptInChange?.(!next);  // revert on failure
      setMsg('Failed: ' + err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleSyncNow = async () => {
    if (!staffId) return;
    setBusy(true); setMsg('');
    try {
      setMsg(summarize(await reconcileOutlook([staffId])));
    } catch (err) {
      setMsg('Failed: ' + err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`app-settings-section${highlight ? ' app-settings-section--pulse' : ''}`}>
      <h3 className="app-settings-section-title">Outlook Calendar Sync</h3>
      <p className="app-settings-section-desc">
        Mirror your RPRS schedule into your Outlook calendar as all-day, non-blocking events
        (they sit at the top of the day and never conflict with meetings). Managed by RPRS —
        edits made in Outlook don't sync back.
      </p>
      <label className="app-settings-toggle" style={{ cursor: busy ? 'wait' : 'pointer' }}>
        <input
          type="checkbox"
          checked={!!optIn}
          disabled={busy || !staffId}
          onChange={(e) => handleToggle(e.target.checked)}
        />
        Sync my RPRS schedule to Outlook
      </label>
      <div className="app-settings-actions">
        <button
          className="app-settings-btn"
          disabled={busy || !optIn}
          onClick={handleSyncNow}
        >
          {busy ? 'Syncing…' : 'Sync my calendar now'}
        </button>
        {msg && (
          <span className={`app-settings-msg${msg.startsWith('Failed') ? ' app-settings-msg--err' : ''}`}>{msg}</span>
        )}
      </div>
    </div>
  );
}
